<template>
  <div class="file-sync-container">
    <!-- 桌面端表格视图 -->
    <div class="desktop-view">
      <!-- 全局同步服务卡片 -->
      <div class="service-card">
        <h3>{{ $t('fileSync.globalSyncService') }}</h3>
        <div class="service-controls">
          <span class="status-badge" :class="{ 'running': globalSwitch }">
            {{ $t(globalSwitch ? 'fileSync.enabled' : 'fileSync.disabled') }}
          </span>
          <button
            @click="globalSwitch ? stopGlobalFileSync() : startGlobalFileSync()"
            :disabled="serviceLoading"
          >
            <span v-if="serviceLoading">{{ $t('fileSync.opening') }}</span>
            <span v-else>{{ $t(globalSwitch ? 'fileSync.close' : 'fileSync.open') }}</span>
          </button>
        </div>
      </div>

      <!-- WiFi同步服务卡片 -->
      <div v-if="isMobileServer" class="service-card">
        <h3>{{ $t('fileSync.wifiSyncService', { network: currentNetwork }) }}</h3>
        <div class="service-controls">
          <span class="status-badge" :class="{ 'running': wifiFileSyncFlag }">
            {{ $t(wifiFileSyncFlag ? 'fileSync.enabled' : 'fileSync.disabled') }}
          </span>
          <button
            @click="wifiFileSyncFlag ? stopWiFiFileSync() : startWiFiFileSync()"
            :disabled="serviceLoading"
          >
            <span v-if="serviceLoading">{{ $t('fileSync.opening') }}</span>
            <span v-else>{{ $t(wifiFileSyncFlag ? 'fileSync.close' : 'fileSync.open') }}</span>
          </button>
        </div>
      </div>

      <!-- 同步任务列表 -->
      <div class="sync-list">
        <div class="list-header">
          <h3>{{ $t('fileSync.syncList') }}</h3>
          <div class="list-actions">
            <button @click="showLogDialog" class="action-btn log-btn">
              📋 {{ $t('fileSync.viewLogs') }}
            </button>
            <button @click="showTaskDialog('add')" class="action-btn add-btn">+ {{ $t('fileSync.addNewTask') }}</button>
          </div>
        </div>

        <div class="table-container">
          <table>
            <thead>
              <tr>
                <th width="60px">{{ $t('fileSync.name') }}</th>
                <th width="200px">{{ $t('fileSync.localPath') }}</th>
                <th width="200px">{{ $t('fileSync.remoteAddr') }}</th>
                <th width="250px">{{ $t('fileSync.remotePath') }}</th>
                <th width="80px">{{ $t('fileSync.remoteUsername') }}</th>
                <th width="80px">{{ $t('fileSync.scanPeriod') }}</th>
                <th width="60px">{{ $t('fileSync.syncMethod') }}</th>
                <th width="300px">{{ $t('fileSync.status') }}</th>
                <th>{{ $t('fileSync.operation') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(task, name) in syncTasks" :key="name" class="task-row">
                <td>{{ name }}</td>
                <td>{{ task.LocalPath }}</td>
                <td>{{ task.RemoteAddr }}</td>
                <td>{{ task.RemotePath }}</td>
                <td>{{ task.RemoteUsername }}</td>
                <td>{{ task.ScanTime }}</td>
                <td class="sync-method">
                  {{ getChannelName(task.Channel) }}
                </td>
                <td class="status">
                  {{ task.Status }}
                </td>
                <td class="actions">
                  <button
                    v-if="task.Switch"
                    @click="stopFileSyncPer(name)"
                    class="action-btn stop-btn"
                  >
                    {{ $t('fileSync.stop') }}
                  </button>
                  <template v-else>
                    <button @click="startFileSyncPer(name)" class="action-btn edit-btn">{{ $t('fileSync.start') }}</button>
                    <button @click="showTaskDialog('edit', name, task)" class="action-btn edit-btn">{{ $t('fileSync.edit') }}</button>
                    <button @click="deleteFileSyncTask(name)" class="action-btn delete-btn">{{ $t('fileSync.delete') }}</button>
                  </template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- 移动端卡片视图 -->
    <div class="mobile-view">
      <!-- 全局同步服务卡片 -->
      <div class="service-card mobile-service-card">
        <div class="mobile-card-header">
          <h3>{{ $t('fileSync.globalSyncService') }}</h3>
          <span class="mobile-status-badge" :class="{ 'running': globalSwitch }">
            {{ $t(globalSwitch ? 'fileSync.enabled' : 'fileSync.disabled') }}
          </span>
        </div>
        <button
          class="mobile-service-btn"
          @click="globalSwitch ? stopGlobalFileSync() : startGlobalFileSync()"
          :disabled="serviceLoading"
        >
          <span v-if="serviceLoading">{{ $t('fileSync.opening') }}</span>
          <span v-else>{{ $t(globalSwitch ? 'fileSync.closeService' : 'fileSync.openService') }}</span>
        </button>
      </div>

      <!-- WiFi同步服务卡片 -->
      <div v-if="isMobileServer" class="service-card mobile-service-card">
        <div class="mobile-card-header">
          <h3>{{ $t('fileSync.wifiSyncService', { network: currentNetwork }) }}</h3>
          <span class="mobile-status-badge" :class="{ 'running': wifiFileSyncFlag }">
            {{ $t(wifiFileSyncFlag ? 'fileSync.enabled' : 'fileSync.disabled') }}
          </span>
        </div>
        <div class="mobile-network-info">
          <span class="network-label">{{ $t('fileSync.networkLabel') }}</span>
          <span class="network-value">{{ currentNetwork }}</span>
        </div>
        <button
          class="mobile-service-btn"
          @click="wifiFileSyncFlag ? stopWiFiFileSync() : startWiFiFileSync()"
          :disabled="serviceLoading"
        >
          <span v-if="serviceLoading">{{ $t('fileSync.opening') }}</span>
          <span v-else>{{ $t(wifiFileSyncFlag ? 'fileSync.closeWiFiSync' : 'fileSync.openWiFiSync') }}</span>
        </button>
      </div>

      <!-- 同步任务列表 -->
      <div class="sync-list mobile-sync-list">
        <div class="mobile-list-header">
          <h3>{{ $t('fileSync.syncList') }}</h3>
          <div class="mobile-actions">
            <button @click="showLogDialog" class="mobile-log-btn">
              📋
            </button>
            <button @click="showTaskDialog('add')" class="mobile-add-btn">
              <span class="plus-icon">+</span>
              <span class="btn-text">{{ $t('fileSync.addTask') }}</span>
            </button>
          </div>
        </div>

        <div class="mobile-task-container" v-if="Object.keys(syncTasks).length > 0">
          <div
            v-for="(task, name) in syncTasks"
            :key="name"
            class="mobile-task-card"
            :class="{ 'task-running': task.Switch }"
          >
            <div class="task-header">
              <h4 class="task-name">{{ name }}</h4>
              <span class="task-status" :class="getStatusClass(task.Status)">
                {{ task.Status }}
              </span>
            </div>

            <div class="task-info">
              <div class="info-row">
                <span class="info-label">{{ $t('fileSync.localPath') }}:</span>
                <span class="info-value path-text">{{ task.LocalPath }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">{{ $t('fileSync.remoteAddr') }}:</span>
                <span class="info-value">{{ task.RemoteAddr }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">{{ $t('fileSync.remotePath') }}:</span>
                <span class="info-value path-text">{{ task.RemotePath }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">{{ $t('fileSync.syncMethod') }}:</span>
                <span class="info-value">{{ getChannelName(task.Channel) }}</span>
              </div>
            </div>

            <div class="task-actions">
              <button
                v-if="task.Switch"
                @click="stopFileSyncPer(name)"
                class="mobile-action-btn stop-btn"
              >
                {{ $t('fileSync.stop') }}
              </button>
              <template v-else>
                <button @click="startFileSyncPer(name)" class="mobile-action-btn start-btn">
                  {{ $t('fileSync.start') }}
                </button>
                <div class="action-group">
                  <button @click="showTaskDialog('edit', name, task)" class="mobile-action-btn edit-btn">
                    {{ $t('fileSync.edit') }}
                  </button>
                  <button @click="deleteFileSyncTask(name)" class="mobile-action-btn delete-btn">
                    {{ $t('fileSync.delete') }}
                  </button>
                </div>
              </template>
            </div>
          </div>
        </div>
        <div v-else class="mobile-empty-state">
          <div class="empty-icon">📁</div>
          <p class="empty-text">{{ $t('fileSync.noTasks') }}</p>
          <button @click="showTaskDialog('add')" class="mobile-add-btn empty-btn">
            + {{ $t('fileSync.addNewTask') }}
          </button>
        </div>
      </div>
    </div>

    <!-- 任务编辑/新增对话框 -->
    <div v-if="showTaskDialogModel" class="dialog-overlay">
      <div class="task-dialog">
        <div class="dialog-header">
          <h3>{{ $t(dialogMode === 'add' ? 'fileSync.addTask' : 'fileSync.editTask') }}</h3>
          <span class="close" @click="closeTaskDialog">&times;</span>
        </div>
        <div class="dialog-content">
          <div class="form-group">
            <label>{{ $t('fileSync.taskName') }}</label>
            <input type="text" v-model="currentTask.name" :placeholder="$t('fileSync.taskNamePlaceholder')" style="width: 200px;" />
          </div>
          <div class="form-group">
            <label>{{ $t('fileSync.localPath') }}</label>
            <div class="path-input">
              <input type="text" v-model="currentTask.localPath" :placeholder="$t('fileSync.localPathPlaceholder')" readonly style="width: 300px;" />
              <button @click="selectDir" class="select-btn">{{ $t('fileSync.select') }}</button>
            </div>
          </div>
          <div class="form-group">
            <label>{{ $t('fileSync.remoteAddr') }}</label>
            <input type="text" v-model="currentTask.remoteAddr" :placeholder="$t('fileSync.remoteAddrPlaceholder')" style="width: 400px;" />
          </div>
          <div class="form-group">
            <label>{{ $t('fileSync.remotePath') }}</label>
            <input type="text" v-model="currentTask.remotePath" :placeholder="$t('fileSync.remotePathPlaceholder')" style="width: 400px;" />
          </div>
          <div class="form-group">
            <label>{{ $t('fileSync.remoteUsername') }}</label>
            <input type="text" v-model="currentTask.remoteUsername" :placeholder="$t('fileSync.usernamePlaceholder')" style="width: 200px;" />
          </div>
          <div class="form-group">
            <label>{{ $t('fileSync.remotePassword') }}</label>
            <div class="password-input">
              <input
                :type="showPassword ? 'text' : 'password'"
                v-model="remotePassword"
                :placeholder="$t(dialogMode === 'edit' ? 'fileSync.leaveEmptyToKeep' : 'fileSync.enterPassword')"
                style="width: 200px;"
              />
              <button @click="showPassword = !showPassword" class="toggle-password">
                {{ $t(showPassword ? 'fileSync.hide' : 'fileSync.show') }}
              </button>
            </div>
            <div v-if="dialogMode === 'edit'" class="password-hint">{{ $t('fileSync.leaveEmptyToKeep') }}</div>
          </div>
          <div class="form-group">
            <label>{{ $t('fileSync.scanPeriod') }}</label>
            <input type="number" v-model="currentTask.scanTime" :placeholder="$t('fileSync.scanPeriodPlaceholder')" min="1" style="width: 200px;" />
          </div>
        </div>
        <div class="dialog-actions">
          <button @click="closeTaskDialog" class="dialog-btn cancel">{{ $t('common.cancel') }}</button>
          <button
            @click="dialogMode === 'add' ? addFileSyncTask() : updateFileSyncTask(currentTask.name)"
            class="dialog-btn confirm"
            :disabled="!isTaskValid"
          >
            {{ $t(dialogMode === 'add' ? 'fileSync.add' : 'common.save') }}
          </button>
        </div>
      </div>
    </div>

    <!-- 移动端任务编辑/新增对话框 -->
    <div v-if="showTaskDialogModel" class="mobile-dialog-overlay">
      <div class="mobile-task-dialog">
        <div class="mobile-dialog-content">
          <h3>{{ $t(dialogMode === 'add' ? 'fileSync.addTask' : 'fileSync.editTask') }}</h3>
          <div class="mobile-form-group">
            <label>{{ $t('fileSync.taskName') }}</label>
            <input
              type="text"
              v-model="currentTask.name"
              :placeholder="$t('fileSync.taskNamePlaceholder')"
              class="mobile-form-input"
            />
          </div>

          <div class="mobile-form-group">
            <label>{{ $t('fileSync.localPath') }}</label>
            <div class="mobile-path-input">
              <input
                type="text"
                v-model="currentTask.localPath"
                :placeholder="$t('fileSync.localPathPlaceholder')"
                readonly
                class="mobile-form-input"
              />
              <button @click="selectDir" class="mobile-select-btn">{{ $t('fileSync.select') }}</button>
            </div>
          </div>

          <div class="mobile-form-group">
            <label>{{ $t('fileSync.remoteAddr') }}</label>
            <input
              type="text"
              v-model="currentTask.remoteAddr"
              :placeholder="$t('fileSync.remoteAddrPlaceholder')"
              class="mobile-form-input"
            />
          </div>

          <div class="mobile-form-group">
            <label>{{ $t('fileSync.remotePath') }}</label>
            <input
              type="text"
              v-model="currentTask.remotePath"
              :placeholder="$t('fileSync.remotePathPlaceholder')"
              class="mobile-form-input"
            />
          </div>

          <div class="mobile-form-group">
            <label>{{ $t('fileSync.remoteUsername') }}</label>
            <input
              type="text"
              v-model="currentTask.remoteUsername"
              :placeholder="$t('fileSync.usernamePlaceholder')"
              class="mobile-form-input"
            />
          </div>

          <div class="mobile-form-group">
            <label>{{ $t('fileSync.remotePassword') }}</label>
            <div class="mobile-password-input">
              <input
                :type="showPassword ? 'text' : 'password'"
                v-model="remotePassword"
                :placeholder="$t(dialogMode === 'edit' ? 'fileSync.leaveEmptyToKeep' : 'fileSync.enterPassword')"
                class="mobile-form-input"
              />
              <button
                @click="showPassword = !showPassword"
                class="mobile-toggle-password"
              >
                {{ $t(showPassword ? 'fileSync.hide' : 'fileSync.show') }}
              </button>
            </div>
            <div v-if="dialogMode === 'edit'" class="mobile-password-hint">
              {{ $t('fileSync.leaveEmptyToKeep') }}
            </div>
          </div>

          <div class="mobile-form-group">
            <label>{{ $t('fileSync.scanPeriod') }}</label>
            <input
              type="number"
              v-model="currentTask.scanTime"
              :placeholder="$t('fileSync.scanPeriodPlaceholder')"
              min="1"
              class="mobile-form-input"
            />
          </div>
        </div>

        <div class="mobile-dialog-actions">
          <button @click="closeTaskDialog" class="mobile-dialog-btn cancel">
            {{ $t('common.cancel') }}
          </button>
          <button
            @click="dialogMode === 'add' ? addFileSyncTask() : updateFileSyncTask(currentTask.name)"
            class="mobile-dialog-btn confirm"
            :disabled="!isTaskValid"
          >
            {{ $t(dialogMode === 'add' ? 'fileSync.add' : 'common.save') }}
          </button>
        </div>
      </div>
    </div>

    <!-- 文件夹选择对话框 -->
    <div v-if="showFolderModal" class="dialog-overlay">
      <div class="directory-dialog">
        <div class="dialog-header">
          <button
            v-if="currentFolderPath !== '/'"
            @click="goToParentFolder"
            class="nav-btn prev-btn"
          >
            {{ $t('fileSync.parentFolder') }}
          </button>
          <h3>{{ $t('fileSync.currentPath') }}: {{ currentFolderPath || '/' }}</h3>
          <span class="close" @click="closeFolderModal">&times;</span>
        </div>

        <!-- 新增手动输入区域 -->
        <div class="manual-input-section">
          <div class="input-header">
            <span class="input-label">{{ $t('fileSync.manualInputPath') }}:</span>
          </div>
          <div class="input-controls">
            <input
              type="text"
              v-model="manualDirectoryPath"
              :placeholder="$t('fileSync.enterDirectoryPath')"
              class="directory-input"
              @keyup.enter="confirmManualDirectory"
            />
            <button
              @click="confirmManualDirectory"
              class="dialog-btn confirm manual-btn"
            >
              {{ $t('fileSync.select') }}
            </button>
          </div>
        </div>

        <div class="dialog-content">
          <div class="directory-tree">
            <ul v-if="folders.length > 0">
              <li
                v-for="folder in folders"
                :key="folder.Path"
                @click="selectFolder(folder)"
                @dblclick="enterFolder(folder)"
                class="directory-entry"
                :class="{ 'selected': selectedFolder && selectedFolder.Path === folder.Path }"
              >
                <span class="folder-icon">📁</span>
                <span class="directory-name">{{ folder.Name }}</span>
                <span class="enter-icon" @click.stop="enterFolder(folder)">→</span>
              </li>
            </ul>
            <div v-else class="empty-directory">
              {{ $t('fileSync.emptyDirectory') }}
            </div>
          </div>
        </div>
        <div class="dialog-actions">
          <div class="selected-path" v-if="selectedFolder || manualDirectoryPath">
            {{ $t('fileSync.selected') }}: {{ selectedFolder ? selectedFolder.Path : manualDirectoryPath }}
          </div>
          <div class="action-buttons">
            <button @click="closeFolderModal" class="dialog-btn cancel">{{ $t('common.cancel') }}</button>
            <button
              @click="confirmFolderSelection"
              class="dialog-btn confirm"
              :disabled="!selectedFolder && !manualDirectoryPath"
            >
              {{ $t('common.confirm') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 移动端文件夹选择对话框 -->
    <div v-if="showFolderModal" class="mobile-dialog-overlay">
      <div class="mobile-directory-dialog">
        <div class="mobile-dialog-header">
          <button
            v-if="currentFolderPath !== '/'"
            @click="goToParentFolder"
            class="mobile-nav-btn"
          >
            {{ $t('fileSync.parentFolderMobile') }}
          </button>
          <h3>{{ $t('fileSync.currentPath') }}: {{ currentFolderPath || '/' }}</h3>
          <button @click="closeFolderModal" class="mobile-close-btn">✕</button>
        </div>

        <!-- 手动输入区域 -->
        <div class="mobile-manual-input">
          <div class="input-label">{{ $t('fileSync.manualInputPathMobile') }}:</div>
          <div class="mobile-input-controls">
            <input
              type="text"
              v-model="manualDirectoryPath"
              :placeholder="$t('fileSync.enterDirectoryPath')"
              class="mobile-directory-input"
              @keyup.enter="confirmManualDirectory"
            />
            <button
              @click="confirmManualDirectory"
              class="mobile-confirm-btn"
            >
              {{ $t('fileSync.select') }}
            </button>
          </div>
        </div>

        <div class="mobile-directory-content">
          <div v-if="folders.length > 0" class="mobile-directory-list">
            <div
              v-for="folder in folders"
              :key="folder.Path"
              @click="selectFolder(folder)"
              @dblclick="enterFolder(folder)"
              class="mobile-directory-item"
              :class="{ 'selected': selectedFolder && selectedFolder.Path === folder.Path }"
            >
              <span class="mobile-folder-icon">📁</span>
              <span class="mobile-directory-name">{{ folder.Name }}</span>
              <button @click.stop="enterFolder(folder)" class="mobile-enter-btn">
                →
              </button>
            </div>
          </div>
          <div v-else class="mobile-empty-directory">
            📁 {{ $t('fileSync.emptyDirectory') }}
          </div>
        </div>

        <div class="mobile-directory-footer">
          <div class="mobile-selected-path" v-if="selectedFolder || manualDirectoryPath">
            {{ $t('fileSync.selected') }}: {{ selectedFolder ? selectedFolder.Path : manualDirectoryPath }}
          </div>
          <div class="mobile-action-buttons">
            <button @click="closeFolderModal" class="mobile-dialog-btn cancel">
              {{ $t('common.cancel') }}
            </button>
            <button
              @click="confirmFolderSelection"
              class="mobile-dialog-btn confirm"
              :disabled="!selectedFolder && !manualDirectoryPath"
            >
              {{ $t('common.confirm') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 日志查看对话框 -->
    <div v-if="logDialogVisible" class="dialog-overlay">
      <div class="log-dialog">
        <div class="dialog-header">
          <h3>{{ $t('fileSync.runningLogs') }}</h3>
          <span class="close" @click="closeLogDialog">&times;</span>
        </div>

        <div class="log-toolbar">
          <div class="log-stats">
            <span>{{ $t('fileSync.totalLogs', { count: logTotal }) }}</span>
            <div class="auto-refresh-control">
              <span class="refresh-label">{{ $t('fileSync.autoRefresh') }}</span>
              <label class="switch">
                <input
                  type="checkbox"
                  v-model="autoRefreshEnabled"
                  @change="toggleAutoRefresh"
                >
                <span class="slider"></span>
              </label>
              <span class="refresh-interval" v-if="autoRefreshEnabled">{{ $t('fileSync.refreshInterval') }}</span>
            </div>
          </div>
          <div class="log-actions">
            <button
              @click="downloadAllLogs"
              :disabled="logDownloading"
              class="log-action-btn download-btn"
            >
              {{ $t(logDownloading ? 'fileSync.downloading' : 'fileSync.downloadAllLogs') }}
            </button>
            <button
              @click="refreshLogs"
              :disabled="logLoading"
              class="log-action-btn refresh-btn"
            >
              {{ $t(logLoading ? 'fileSync.refreshing' : 'fileSync.refreshNow') }}
            </button>
            <button
              @click="clearLogs"
              :disabled="logClearing"
              class="log-action-btn clear-btn"
            >
              {{ $t(logClearing ? 'fileSync.clearing' : 'fileSync.clearLogs') }}
            </button>
          </div>
        </div>

        <div class="log-content" ref="logContentRef">
          <div v-if="logsData.length === 0" class="empty-log">
            <div class="empty-icon">📝</div>
            <p>{{ $t('fileSync.noLogs') }}</p>
          </div>
          <div v-else class="log-list">
            <div
              v-for="(log, index) in logsData"
              :key="index"
              class="log-item"
              :class="`log-level-${log.Level.toLowerCase()}`"
            >
              <span class="log-time">{{ log.Time }}</span>
              <span class="log-level" :class="`level-${log.Level.toLowerCase()}`">
                [{{ log.Level }}]
              </span>
              <span class="log-message">{{ log.Content }}</span>
            </div>
          </div>
        </div>

        <div class="log-pagination" v-if="logTotal > 0">
          <div class="pagination-controls">
            <button
              @click="changeLogPage(logCurrentPage - 1)"
              :disabled="logCurrentPage <= 1"
              class="page-btn"
            >‹</button>
            <span class="page-info">
              <select v-model="logCurrentPage" @change="handleLogPageChange">
                <option v-for="page in totalPages" :key="page" :value="page">
                  {{ page }}
                </option>
              </select>
              / {{ totalPages }}
            </span>
            <button
              @click="changeLogPage(logCurrentPage + 1)"
              :disabled="logCurrentPage >= totalPages"
              class="page-btn"
            >›</button>
            <select v-model="logPageSize" @change="handleLogSizeChange" class="page-size-select">
              <option value="20">20 {{ $t('fileSync.perPage') }}</option>
              <option value="50">50 {{ $t('fileSync.perPage') }}</option>
              <option value="100">100 {{ $t('fileSync.perPage') }}</option>
              <option value="200">200 {{ $t('fileSync.perPage') }}</option>
            </select>
          </div>
        </div>

        <div class="dialog-actions">
          <button @click="closeLogDialog" class="dialog-btn cancel">{{ $t('common.close') }}</button>
        </div>
      </div>
    </div>

    <!-- 移动端日志查看对话框 -->
    <div v-if="logDialogVisible" class="mobile-dialog-overlay">
      <div class="mobile-log-dialog">
        <div class="mobile-dialog-header">
          <h3>{{ $t('fileSync.syncLogs') }}</h3>
          <button @click="closeLogDialog" class="mobile-close-btn">✕</button>
        </div>

        <div class="mobile-log-toolbar">
          <div class="mobile-log-stats">
            <span>{{ $t('fileSync.totalLogsMobile', { count: logTotal }) }}</span>
            <div class="mobile-auto-refresh">
              <span>{{ $t('fileSync.autoRefresh') }}</span>
              <label class="mobile-switch">
                <input
                  type="checkbox"
                  v-model="autoRefreshEnabled"
                  @change="toggleAutoRefresh"
                >
                <span class="slider"></span>
              </label>
            </div>
          </div>
          <div class="mobile-log-actions">
            <button
              @click="refreshLogs"
              :disabled="logLoading"
              class="mobile-log-btn refresh"
            >
              🔄
            </button>
            <button
              @click="downloadAllLogs"
              :disabled="logDownloading"
              class="mobile-log-btn download"
            >
              📥
            </button>
            <button
              @click="clearLogs"
              :disabled="logClearing"
              class="mobile-log-btn clear"
            >
              🗑️
            </button>
          </div>
        </div>

        <div class="mobile-log-content" ref="mobileLogContentRef">
          <div v-if="logsData.length === 0" class="mobile-empty-log">
            <div class="empty-icon">📝</div>
            <p>{{ $t('fileSync.noLogs') }}</p>
          </div>
          <div v-else class="mobile-log-list">
            <div
              v-for="(log, index) in logsData"
              :key="index"
              class="mobile-log-item"
              :class="`log-level-${log.Level.toLowerCase()}`"
            >
              <div class="log-header">
                <span class="log-time">{{ log.Time }}</span>
                <span class="log-level" :class="`level-${log.Level.toLowerCase()}`">
                  [{{ log.Level }}]
                </span>
              </div>
              <div class="log-message">{{ log.Content }}</div>
            </div>
          </div>
        </div>

        <div class="mobile-log-pagination" v-if="logTotal > 0">
          <div class="mobile-pagination-controls">
            <button
              @click="changeLogPage(logCurrentPage - 1)"
              :disabled="logCurrentPage <= 1"
              class="mobile-page-btn"
            >‹</button>
            <span class="mobile-page-info">
              <select v-model="logCurrentPage" @change="handleLogPageChange" class="mobile-page-select">
                <option v-for="page in totalPages" :key="page" :value="page">
                  {{ page }}
                </option>
              </select>
              / {{ totalPages }}
            </span>
            <button
              @click="changeLogPage(logCurrentPage + 1)"
              :disabled="logCurrentPage >= totalPages"
              class="mobile-page-btn"
            >›</button>
          </div>
        </div>

        <div class="mobile-dialog-actions">
          <button @click="closeLogDialog" class="mobile-dialog-btn cancel full-width">
            {{ $t('common.close') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { API_BASE_URL } from '@/config';
import { ElMessage, ElMessageBox } from 'element-plus'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

export default {
  setup() {
    const { t } = useI18n()
    const router = useRouter()
    
    // 状态
    const globalSwitch = ref(false);
    const wifiFileSyncFlag = ref(false);
    const isMobileServer = ref(false);
    const currentNetwork = ref('');
    const syncTasks = ref({});
    const serviceLoading = ref(false);
    const refreshInterval = ref(null);
    const logRefreshInterval = ref(null);
    const showPassword = ref(false);
    
    // 任务对话框状态
    const dialogMode = ref('add'); // 'add' or 'edit'
    const currentTask = ref({
      name: '',
      localPath: '',
      remoteAddr: '',
      remotePath: '',
      remoteUsername: '',
      remotePassword: '',
      scanTime: '60'
    });

    const remotePassword = ref('');
    
    // 文件夹对话框状态
    const showFolderModal = ref(false);
    const showTaskDialogModel = ref(false);
    const currentFolderPath = ref('/');
    const folders = ref([]);
    const selectedFolder = ref(null);
    const manualDirectoryPath = ref('');

    // 日志对话框状态
    const logDialogVisible = ref(false);
    const logLoading = ref(false);
    const logClearing = ref(false);
    const logDownloading = ref(false);
    const logsData = ref([]);
    const logTotal = ref(0);
    const logCurrentPage = ref(1);
    const logPageSize = ref(50);
    const logContentRef = ref(null);
    const mobileLogContentRef = ref(null);
    const autoRefreshEnabled = ref(true);

    // 计算属性
    const isTaskValid = computed(() => {
      const task = currentTask.value;
      return (
        task.name && /^[a-zA-Z0-9]{3,15}$/.test(task.name) &&
        task.localPath &&
        task.remoteAddr &&
        task.remotePath &&
        task.remoteUsername &&
        task.scanTime
      );
    });

    const totalPages = computed(() => {
      return Math.ceil(logTotal.value / logPageSize.value);
    });
    
    // 获取数据
    const fetchData = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/file-sync`, {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          },
          credentials: 'include'
        });
        const data = await response.json();
        
        if (data.errCode === 0) {
          globalSwitch.value = data.globalSwitch;
          wifiFileSyncFlag.value = data.wifiFileSyncFlag;
          isMobileServer.value = data.isMobileServer;
          currentNetwork.value = data.currentNetwork;
          syncTasks.value = data.data || {};
        }
        
        if (data.errCode === 1000) {
          ElMessage.error(t('common.pleaseLoginFirst'));
          router.push('/user-login?callback=file-sync-mgmt');
          return;
        }

        if (data.errCode != 0) {
          ElMessage.error(data.errMsg)
          return
        }

      } catch (error) {
        console.error(t('fileSync.fetchDataFailed'), error);
      }
    };
    
    // 设置自动刷新
    const setupAutoRefresh = () => {
      if (refreshInterval.value) {
        clearInterval(refreshInterval.value);
      }
      refreshInterval.value = setInterval(fetchData, 3000);
    };
    
    // 全局同步服务控制
    const startGlobalFileSync = async () => {
      serviceLoading.value = true;
      try {
        const response = await fetch(`${API_BASE_URL}/start-global-file-sync`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          },
          credentials: 'include'
        });
        const data = await response.json();
        if (data.errCode === 0) {
          fetchData();
        }
      } catch (error) {
        console.error(t('fileSync.operationFailed'), error);
      } finally {
        serviceLoading.value = false;
      }
    };

    const stopGlobalFileSync = async () => {
      serviceLoading.value = true;
      try {
        const response = await fetch(`${API_BASE_URL}/stop-global-file-sync`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          },
          credentials: 'include'
        });
        const data = await response.json();
        if (data.errCode === 0) {
          fetchData();
        }
      } catch (error) {
        console.error(t('fileSync.operationFailed'), error);
      } finally {
        serviceLoading.value = false;
      }
    };
    
    // WiFi同步服务控制
    const startWiFiFileSync = async () => {
      serviceLoading.value = true;
      try {
        const response = await fetch(`${API_BASE_URL}/start-wifi-file-sync`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          },
          credentials: 'include'
        });
        const data = await response.json();
        if (data.errCode === 0) {
          fetchData();
        }
      } catch (error) {
        console.error(t('fileSync.operationFailed'), error);
      } finally {
        serviceLoading.value = false;
      }
    };
    
    const stopWiFiFileSync = async () => {
      serviceLoading.value = true;
      try {
        const response = await fetch(`${API_BASE_URL}/stop-wifi-file-sync`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          },
          credentials: 'include'
        });
        const data = await response.json();
        if (data.errCode === 0) {
          fetchData();
        }
      } catch (error) {
        console.error(t('fileSync.operationFailed'), error);
      } finally {
        serviceLoading.value = false;
      }
    };
    
    // 任务对话框控制
    const showTaskDialog = (mode, taskName = null, taskData = null) => {
      dialogMode.value = mode;
      remotePassword.value = ''
      
      if (mode === 'edit' && taskName && taskData) {
        currentTask.value = {
          name: taskName,
          localPath: taskData.LocalPath,
          remoteAddr: taskData.RemoteAddr,
          remotePath: taskData.RemotePath,
          remoteUsername: taskData.RemoteUsername,
          remotePassword: taskData.RemotePassword || '',
          scanTime: taskData.ScanTime.toString()
        };
      } else {
        resetCurrentTask();
      }
      
      showTaskDialogModel.value = true;
    };
    
    const closeTaskDialog = () => {
      showTaskDialogModel.value = false;
      resetCurrentTask();
    };
    
    const resetCurrentTask = () => {
      currentTask.value = {
        name: '',
        localPath: '',
        remoteAddr: '',
        remotePath: '',
        remoteUsername: '',
        remotePassword: '',
        scanTime: '60'
      };
    };
    
    // 同步任务管理
    const addFileSyncTask = async () => {
      if (!isTaskValid.value) return;
      
      try {
        const formData = new URLSearchParams();
        formData.append('name', currentTask.value.name);
        formData.append('localPath', currentTask.value.localPath);
        formData.append('remoteAddr', currentTask.value.remoteAddr);
        formData.append('remotePath', currentTask.value.remotePath);
        formData.append('remoteUsername', currentTask.value.remoteUsername);
        formData.append('remotePassword', remotePassword.value);
        formData.append('scanTime', currentTask.value.scanTime);
        
        const response = await fetch(`${API_BASE_URL}/add-file-sync-task`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          },
          body: formData,
          credentials: 'include'
        });
        
        const data = await response.json();
        if (data.errCode === 0) {
          closeTaskDialog();
          fetchData();
        }
      } catch (error) {
        console.error(t('fileSync.addTaskFailed'), error);
      }
    };
    
    const updateFileSyncTask = async (taskName) => {
      if (!isTaskValid.value) return;
      
      try {
        const formData = new URLSearchParams();
        formData.append('name', taskName);
        formData.append('localPath', currentTask.value.localPath);
        formData.append('remoteAddr', currentTask.value.remoteAddr);
        formData.append('remotePath', currentTask.value.remotePath);
        formData.append('remoteUsername', currentTask.value.remoteUsername);
        formData.append('remotePassword', remotePassword.value);
        formData.append('scanTime', currentTask.value.scanTime);
        
        const response = await fetch(`${API_BASE_URL}/update-file-sync-task`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          },
          body: formData,
          credentials: 'include'
        });
        
        const data = await response.json();
        if (data.errCode === 0) {
          closeTaskDialog();
          fetchData();
        }
      } catch (error) {
        console.error(t('fileSync.updateTaskFailed'), error);
      }
    };
    
    const deleteFileSyncTask = async (taskName) => {
      try {
        await ElMessageBox.confirm(
          t('fileSync.confirmDeleteTask', { name: taskName }),
          t('fileSync.warning'),
          {
            confirmButtonText: t('common.confirm'),
            cancelButtonText: t('common.cancel'),
            type: 'warning'
          }
        );

        const response = await fetch(`${API_BASE_URL}/delete-file-sync-task?name=${taskName}`, {
          method: 'DELETE',
          credentials: 'include'
        });
        const data = await response.json();

        if (data.errCode === 1000) {
          ElMessage.error(t('common.pleaseLoginFirst'));
          router.push('/user-login?callback=file-sync-mgmt');
          return;
        }

        if (data.errCode !== 0) {
          ElMessage.error(data.errMsg);
          return;
        }
        ElMessage.success(t('fileSync.deleteSuccess'));

        fetchData();
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error(error.message);
        }
      }
    };
    
    const startFileSyncPer = async (taskName) => {
      try {
        const response = await fetch(`${API_BASE_URL}/start-file-sync`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          },
          body: `name=${taskName}`,
          credentials: 'include'
        });
        const data = await response.json();
        if (data.errCode === 0) {
          fetchData();
        }
      } catch (error) {
        console.error(t('fileSync.startTaskFailed'), error);
      }
    };

    const stopFileSyncPer = async (taskName) => {
      try {
        const response = await fetch(`${API_BASE_URL}/stop-file-sync`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          },
          body: `name=${taskName}`,
          credentials: 'include'
        });
        const data = await response.json();
        if (data.errCode === 0) {
          fetchData();
        }
      } catch (error) {
        console.error(t('fileSync.stopTaskFailed'), error);
      }
    };
    
    // 文件夹选择相关方法
    const openFolderModal = () => {
      showFolderModal.value = true;
      currentFolderPath.value = '/';
      fetchFolders(currentFolderPath.value);
    };
    
    const closeFolderModal = () => {
      showFolderModal.value = false;
      selectedFolder.value = null;
      manualDirectoryPath.value = '';
    };
    
    const fetchFolders = async (path) => {
      try {
        const response = await fetch(`${API_BASE_URL}/get-dirList?path=${encodeURIComponent(path)}`, {
          credentials: 'include'
        });
        const data = await response.json();
        folders.value = data.file || [];
      } catch (error) {
        console.error(t('fileSync.fetchFoldersFailed'), error);
        folders.value = [];
      }
    };
    
    const selectFolder = (folder) => {
      selectedFolder.value = folder;
    };
    
    const enterFolder = (folder) => {
      currentFolderPath.value = folder.Path;
      fetchFolders(currentFolderPath.value);
    };
    
    const goToParentFolder = () => {
      const parts = currentFolderPath.value.split('/');
      parts.pop();
      currentFolderPath.value = parts.join('/') || '/';
      fetchFolders(currentFolderPath.value);
    };
    
    const confirmManualDirectory = () => {
      if (manualDirectoryPath.value.trim()) {
        ElMessage.success(t('fileSync.directorySelected', { path: manualDirectoryPath.value }));
      }
    };

    const confirmFolderSelection = () => {
      let selectedPath = '';

      if (selectedFolder.value) {
        selectedPath = selectedFolder.value.Path;
      } else if (manualDirectoryPath.value) {
        selectedPath = manualDirectoryPath.value.trim();
      } else {
        ElMessage.warning(t('fileSync.pleaseSelectDirectory'));
        return;
      }

      currentTask.value.localPath = selectedPath;
      closeFolderModal();
    };
    
    const selectDir = () => {
      openFolderModal();
    };
    
    const getChannelName = (channel) => {
      switch (channel) {
        case 1: return 'webdav';
        case 2: return 'http';
        default: return 'none';
      }
    };
    
    // 状态样式类
    const getStatusClass = (status) => {
      if (status.includes(t('common.running')) || status.includes(t('fileSync.enabled')) || status.includes(t('fileSync.start'))) return 'status-running';
      if (status.includes(t('common.stopped')) || status.includes(t('fileSync.disabled')) || status.includes(t('fileSync.stop'))) return 'status-stopped';
      if (status.includes(t('fileSync.syncing'))) return 'status-syncing';
      return '';
    };

    // 日志相关方法
    const showLogDialog = () => {
      logDialogVisible.value = true;
      fetchLogs();
      if (autoRefreshEnabled.value) {
        startLogAutoRefresh();
      }
    };

    const closeLogDialog = () => {
      logDialogVisible.value = false;
      stopLogAutoRefresh();
    };

    const toggleAutoRefresh = (enabled) => {
      if (enabled) {
        startLogAutoRefresh();
        ElMessage.success(t('fileSync.autoRefreshEnabled'));
      } else {
        stopLogAutoRefresh();
        ElMessage.info(t('fileSync.autoRefreshDisabled'));
      }
    };

    const startLogAutoRefresh = () => {
      stopLogAutoRefresh();
      logRefreshInterval.value = setInterval(() => {
        if (logDialogVisible.value) {
          fetchLogs(false);
        }
      }, 5000);
    };

    const stopLogAutoRefresh = () => {
      if (logRefreshInterval.value) {
        clearInterval(logRefreshInterval.value);
        logRefreshInterval.value = null;
      }
    };

    const fetchLogs = async (showLoading = true) => {
      try {
        if (showLoading) {
          logLoading.value = true;
        }
        
        const params = new URLSearchParams({
          module: 'filesync',
          page: logCurrentPage.value,
          size: logPageSize.value
        });
        
        const response = await fetch(`${API_BASE_URL}/get-moduleLog?${params.toString()}`, {
          credentials: 'include'
        });

        if (!response.ok) throw new Error(t('fileSync.networkError'));

        const data = await response.json();

        if (data.errCode === 1000) {
          ElMessage.error(t('common.pleaseLoginFirst'));
          router.push('/user-login?callback=file-sync-mgmt');
          return;
        }

        if (data.errCode !== 0) {
          throw new Error(data.errMsg || t('fileSync.fetchLogsFailed'));
        }

        logsData.value = data.data || [];
        logTotal.value = data.count || 0;

        if (showLoading) {
          if (logContentRef.value) {
            logContentRef.value.scrollTop = 0;
          }
          if (mobileLogContentRef.value) {
            mobileLogContentRef.value.scrollTop = 0;
          }
        }
      } catch (error) {
        console.error(t('fileSync.fetchLogsFailed'), error);
        if (showLoading) {
          ElMessage.error(t('fileSync.fetchLogsFailedDetail', { error: error.message || t('fileSync.unknownError') }));
        }
      } finally {
        if (showLoading) {
          logLoading.value = false;
        }
      }
    };

    const refreshLogs = () => {
      fetchLogs(true);
    };

    const clearLogs = async () => {
      try {
        await ElMessageBox.confirm(t('fileSync.confirmClearLogs'), t('fileSync.warning'), {
          confirmButtonText: t('common.confirm'),
          cancelButtonText: t('common.cancel'),
          type: 'warning'
        });

        logClearing.value = true;

        const params = new URLSearchParams({
          module: 'filesync'
        });

        const response = await fetch(`${API_BASE_URL}/clear-moduleLog?${params.toString()}`, {
          method: 'DELETE',
          credentials: 'include'
        });

        if (!response.ok) throw new Error(t('fileSync.networkError'));

        const data = await response.json();

        if (data.errCode === 1000) {
          ElMessage.error(t('common.pleaseLoginFirst'));
          router.push('/user-login?callback=file-sync-mgmt');
          return;
        }

        if (data.errCode !== 0) {
          throw new Error(data.errMsg || t('fileSync.clearLogsFailed'));
        }

        ElMessage.success(t('fileSync.logsCleared'));
        fetchLogs(true);
      } catch (error) {
        if (error !== 'cancel') {
          console.error(t('fileSync.clearLogsFailed'), error);
          ElMessage.error(t('fileSync.clearLogsFailedDetail', { error: error.message || t('fileSync.unknownError') }));
        }
      } finally {
        logClearing.value = false;
      }
    };

    const downloadAllLogs = async () => {
      try {
        logDownloading.value = true;

        const params = new URLSearchParams({
          module: 'filesync',
          page: 1,
          size: 10000
        });

        const response = await fetch(`${API_BASE_URL}/get-moduleLog?${params.toString()}`, {
          credentials: 'include'
        });

        if (!response.ok) throw new Error(t('fileSync.networkError'));

        const data = await response.json();

        if (data.errCode === 1000) {
          ElMessage.error(t('common.pleaseLoginFirst'));
          router.push('/user-login?callback=file-sync-mgmt');
          return;
        }

        if (data.errCode !== 0) {
          throw new Error(data.errMsg || t('fileSync.fetchLogsFailed'));
        }

        const allLogs = data.data || [];

        if (allLogs.length === 0) {
          ElMessage.warning(t('fileSync.noLogsToDownload'));
          return;
        }

        let logContent = '';
        allLogs.forEach(log => {
          logContent += `[${log.Time}] [${log.Level}] ${log.Content}\n`;
        });

        const blob = new Blob([logContent], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `file_sync_logs_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.log`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        ElMessage.success(t('fileSync.downloadSuccess', { count: allLogs.length }));
      } catch (error) {
        console.error(t('fileSync.downloadLogsFailed'), error);
        ElMessage.error(t('fileSync.downloadLogsFailedDetail', { error: error.message || t('fileSync.unknownError') }));
      } finally {
        logDownloading.value = false;
      }
    };

    const handleLogSizeChange = (event) => {
      logPageSize.value = parseInt(event.target.value);
      logCurrentPage.value = 1;
      fetchLogs(true);
    };

    const handleLogPageChange = () => {
      fetchLogs(true);
    };

    const changeLogPage = (page) => {
      if (page >= 1 && page <= totalPages.value) {
        logCurrentPage.value = page;
        fetchLogs(true);
      }
    };
    
    // 生命周期钩子
    onMounted(() => {
      fetchData();
      setupAutoRefresh();
    });
    
    onUnmounted(() => {
      if (refreshInterval.value) {
        clearInterval(refreshInterval.value);
      }
      stopLogAutoRefresh();
    });
    
    return {
      // 状态
      globalSwitch,
      wifiFileSyncFlag,
      isMobileServer,
      currentNetwork,
      syncTasks,
      serviceLoading,
      showTaskDialog,
      dialogMode,
      currentTask,
      remotePassword,
      isTaskValid,
      showFolderModal,
      showTaskDialogModel,
      currentFolderPath,
      folders,
      selectedFolder,
      showPassword,
      manualDirectoryPath,
      
      // 日志状态
      logDialogVisible,
      logLoading,
      logClearing,
      logDownloading,
      logsData,
      logTotal,
      logCurrentPage,
      logPageSize,
      logContentRef,
      mobileLogContentRef,
      autoRefreshEnabled,
      totalPages,

      // 方法
      startGlobalFileSync,
      stopGlobalFileSync,
      startWiFiFileSync,
      stopWiFiFileSync,
      closeTaskDialog,
      addFileSyncTask,
      updateFileSyncTask,
      deleteFileSyncTask,
      startFileSyncPer,
      stopFileSyncPer,
      selectFolder,
      enterFolder,
      goToParentFolder,
      confirmFolderSelection,
      closeFolderModal,
      selectDir,
      getChannelName,
      confirmManualDirectory,
      getStatusClass,
      
      // 日志方法
      showLogDialog,
      closeLogDialog,
      toggleAutoRefresh,
      refreshLogs,
      clearLogs,
      downloadAllLogs,
      handleLogSizeChange,
      handleLogPageChange,
      changeLogPage
    };
  }
};
</script>

<style scoped>
/* 基础样式 */
.file-sync-container {
  padding: 20px;
  font-family: 'Helvetica Neue', Arial, sans-serif;
  background-color: #f5f7fa;
  min-height: 100vh;
}

/* ========== 桌面端样式 ========== */
.desktop-view {
  display: block;
}

/* 服务卡片样式 */
.service-card {
  background: white;
  border-radius: 8px;
  padding: 15px 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s, box-shadow 0.2s;
}

.service-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.service-card h3 {
  margin: 0 0 10px 0;
  color: #333;
  font-size: 16px;
  font-weight: 600;
}

.service-controls {
  display: flex;
  align-items: center;
  gap: 12px;
}

.status-badge {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.status-badge.running {
  background: #e6f7e6;
  color: #4caf50;
}

.status-badge:not(.running) {
  background: #f0f0f0;
  color: #757575;
}

.service-controls button {
  padding: 6px 12px;
  background: #f0f0f0;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  transition: background 0.2s;
}

.service-controls button:hover {
  background: #e0e0e0;
}

/* 同步列表样式 */
.sync-list {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.list-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.list-actions {
  display: flex;
  gap: 10px;
}

.action-btn {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s;
}

.add-btn {
  background: #f0f7ff;
  color: #1890ff;
  border: 1px dashed #91d5ff;
}

.add-btn:hover {
  background: #e6f7ff;
  border-color: #69c0ff;
}

.log-btn {
  background: #f0f7ff;
  color: #1890ff;
  border: 1px solid #91d5ff;
}

.log-btn:hover {
  background: #e6f7ff;
}

.edit-btn {
  background: #f0f7ff;
  color: #1890ff;
  border: 1px solid #d9e9ff;
}

.edit-btn:hover {
  background: #e6f7ff;
}

.stop-btn {
  background: #fff2f0;
  color: #ff4d4f;
  border: 1px solid #ffccc7;
}

.stop-btn:hover {
  background: #ffece8;
}

.delete-btn {
  background: #fff2f0;
  color: #ff4d4f;
  border: 1px solid #ffccc7;
}

.delete-btn:hover {
  background: #ffece8;
}

.table-container {
  overflow-x: auto;
  border-radius: 6px;
  border: 1px solid #eee;
}

table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

th, td {
  padding: 8px 12px;
  text-align: left;
  border-bottom: 1px solid #eee;
}

th {
  background-color: #fafafa;
  font-weight: 500;
  color: #555;
  white-space: nowrap;
}

.task-row {
  transition: background 0.2s;
}

.task-row:hover {
  background-color: #f9f9f9;
}

.sync-method, .status {
  font-size: 12px;
  color: #666;
}

.actions {
  display: flex;
  gap: 8px;
}

/* 任务对话框样式 */
.task-dialog {
  background: white;
  border-radius: 8px;
  width: 600px;
  max-width: 95%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

.dialog-header {
  padding: 12px 16px;
  border-bottom: 1px solid #eee;
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fafafa;
  position: relative;
}

.dialog-header h3 {
  margin: 0;
  font-size: 15px;
  font-weight: 500;
  flex-grow: 1;
  color: #333;
}

.close {
  color: #aaa;
  font-size: 24px;
  font-weight: bold;
  cursor: pointer;
  position: absolute;
  right: 16px;
  top: 10px;
}

.close:hover {
  color: #333;
}

.dialog-content {
  padding: 20px;
  overflow-y: auto;
  flex-grow: 1;
}

.form-group {
  margin-bottom: 18px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  color: #555;
  font-weight: 500;
}

.form-group input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.form-group input:focus {
  border-color: #1890ff;
  outline: none;
}

.path-input {
  display: flex;
  gap: 10px;
}

.path-input input {
  flex-grow: 1;
  background-color: #f9f9f9;
}

.select-btn {
  padding: 10px 15px;
  background: #f0f7ff;
  color: #1890ff;
  border: 1px solid #d9e9ff;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s;
  white-space: nowrap;
}

.select-btn:hover {
  background: #e6f7ff;
  border-color: #69c0ff;
}

.dialog-actions {
  padding: 12px 16px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  background: #fafafa;
}

.dialog-btn {
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  min-width: 80px;
  transition: all 0.2s;
}

.dialog-btn.cancel {
  background: white;
  border: 1px solid #d9d9d9;
  color: #333;
}

.dialog-btn.cancel:hover {
  border-color: #1890ff;
  color: #1890ff;
}

.dialog-btn.confirm {
  background: #1890ff;
  color: white;
  border: none;
}

.dialog-btn.confirm:disabled {
  background: #bae0ff;
  cursor: not-allowed;
}

.dialog-btn.confirm:not(:disabled):hover {
  background: #40a9ff;
}

/* 文件夹选择对话框样式 */
.directory-dialog {
  background: white;
  border-radius: 8px;
  width: 500px;
  max-width: 90%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

.directory-dialog .dialog-header {
  padding: 12px 16px;
  border-bottom: 1px solid #eee;
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fafafa;
  position: relative;
}

.nav-btn {
  padding: 4px 10px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
}

.nav-btn:hover {
  border-color: #1890ff;
  color: #1890ff;
}

.directory-tree ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.directory-entry {
  padding: 8px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  position: relative;
  transition: background 0.2s;
  border-bottom: 1px solid #f5f5f5;
  font-size: 13px;
}

.directory-entry:hover {
  background: #f5f5f5;
}

.directory-entry.selected {
  background: #e6f7ff;
}

.folder-icon {
  font-size: 16px;
  color: #ffc53d;
}

.directory-name {
  flex-grow: 1;
}

.enter-icon {
  color: #999;
  padding: 3px;
  border-radius: 4px;
  opacity: 0;
  transition: all 0.2s;
  font-size: 14px;
}

.directory-entry:hover .enter-icon {
  opacity: 1;
}

.enter-icon:hover {
  background: #e6f7ff;
  color: #1890ff;
}

.empty-directory {
  text-align: center;
  color: #999;
  padding: 30px 20px;
  font-size: 13px;
}

.selected-path {
  font-size: 12px;
  color: #666;
  flex-grow: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-right: 10px;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

/* 对话框遮罩 */
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

/* 密码输入框样式 */
.password-input {
  display: flex;
  align-items: center;
  gap: 8px;
}

.password-input input {
  width: 200px;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 13px;
}

.toggle-password {
  padding: 8px 12px;
  background: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
}

.toggle-password:hover {
  background: #eaeaea;
}

.password-hint {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
}

/* 手动输入区域样式 */
.manual-input-section {
  padding: 12px 16px;
  border-bottom: 1px solid #eee;
  background: #fafafa;
}

.input-header {
  margin-bottom: 8px;
}

.input-label {
  font-size: 13px;
  font-weight: 500;
  color: #333;
}

.input-controls {
  display: flex;
  gap: 8px;
  align-items: center;
}

.directory-input {
  flex-grow: 1;
  padding: 6px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 13px;
  outline: none;
  transition: border-color 0.2s;
}

.directory-input:focus {
  border-color: #1890ff;
}

.directory-input::placeholder {
  color: #999;
}

.manual-btn {
  min-width: 60px;
  white-space: nowrap;
}

/* 日志对话框样式 */
.log-dialog {
  background: white;
  border-radius: 8px;
  width: 900px;
  max-width: 95%;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

.log-toolbar {
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fafafa;
}

.log-stats {
  display: flex;
  align-items: center;
  gap: 24px;
  font-size: 13px;
  color: #666;
}

.auto-refresh-control {
  display: flex;
  align-items: center;
  gap: 8px;
}

.refresh-label {
  font-size: 12px;
  color: #999;
}

.refresh-interval {
  font-size: 12px;
  color: #4caf50;
}

.log-actions {
  display: flex;
  gap: 8px;
}

.log-action-btn {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
}

.log-action-btn.download-btn {
  background: #f6ffed;
  color: #52c41a;
  border: 1px solid #b7eb8f;
}

.log-action-btn.download-btn:hover:not(:disabled) {
  background: #d9f7be;
}

.log-action-btn.refresh-btn {
  background: #e6f7ff;
  color: #1890ff;
  border: 1px solid #91d5ff;
}

.log-action-btn.refresh-btn:hover:not(:disabled) {
  background: #bae7ff;
}

.log-action-btn.clear-btn {
  background: #fff2f0;
  color: #ff4d4f;
  border: 1px solid #ffccc7;
}

.log-action-btn.clear-btn:hover:not(:disabled) {
  background: #ffece8;
}

.log-action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.log-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
  background: #fafafa;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 13px;
  line-height: 1.6;
  min-height: 300px;
  max-height: 400px;
}

.empty-log {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #999;
  text-align: center;
}

.empty-log .empty-icon {
  font-size: 40px;
  margin-bottom: 12px;
}

.empty-log p {
  margin: 0;
  font-size: 14px;
}

.log-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.log-item {
  padding: 8px 12px;
  border-bottom: 1px solid #eee;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: white;
  border-radius: 4px;
}

.log-item.log-level-error {
  background: #fff2f0;
}

.log-item.log-level-warn {
  background: #fffbe6;
}

.log-time {
  color: #999;
  font-size: 12px;
  white-space: nowrap;
  min-width: 160px;
}

.log-level {
  font-weight: 600;
  font-size: 12px;
  white-space: nowrap;
  min-width: 60px;
}

.log-level.level-error {
  color: #ff4d4f;
}

.log-level.level-warn {
  color: #faad14;
}

.log-level.level-info {
  color: #1890ff;
}

.log-message {
  color: #333;
  word-break: break-all;
  flex: 1;
}

.log-pagination {
  padding: 16px 20px;
  border-top: 1px solid #eee;
  background: #fafafa;
}

.pagination-controls {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
}

.page-btn {
  padding: 4px 10px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.page-btn:hover:not(:disabled) {
  border-color: #1890ff;
  color: #1890ff;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: #666;
}

.page-info select {
  padding: 4px 8px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 13px;
  outline: none;
}

.page-info select:focus {
  border-color: #1890ff;
}

.page-size-select {
  padding: 4px 8px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 13px;
  outline: none;
}

.page-size-select:focus {
  border-color: #1890ff;
}

/* 开关样式 */
.switch {
  position: relative;
  display: inline-block;
  width: 40px;
  height: 20px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: .4s;
  border-radius: 20px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 16px;
  width: 16px;
  left: 2px;
  bottom: 2px;
  background-color: white;
  transition: .4s;
  border-radius: 50%;
}

input:checked + .slider {
  background-color: #1890ff;
}

input:checked + .slider:before {
  transform: translateX(20px);
}

/* ========== 移动端样式 ========== */
.mobile-view {
  display: none;
}

/* 移动端卡片样式 */
.mobile-service-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.mobile-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.mobile-card-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.mobile-status-badge {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}

.mobile-status-badge.running {
  background: linear-gradient(135deg, #d4edda, #c3e6cb);
  color: #155724;
}

.mobile-status-badge:not(.running) {
  background: linear-gradient(135deg, #f8d7da, #f5c6cb);
  color: #721c24;
}

.mobile-network-info {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 8px;
  font-size: 14px;
}

.network-label {
  color: #666;
  margin-right: 8px;
}

.network-value {
  color: #1890ff;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mobile-service-btn {
  width: 100%;
  padding: 14px;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  background: linear-gradient(135deg, #1890ff, #096dd9);
  color: white;
  box-shadow: 0 2px 8px rgba(24, 144, 255, 0.3);
}

.mobile-service-btn:disabled {
  background: linear-gradient(135deg, #ccc, #bbb);
  cursor: not-allowed;
  box-shadow: none;
}

.mobile-service-btn:not(:disabled):active {
  transform: scale(0.98);
  box-shadow: 0 1px 4px rgba(24, 144, 255, 0.3);
}

/* 移动端同步列表样式 */
.mobile-sync-list {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.mobile-list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.mobile-list-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.mobile-actions {
  display: flex;
  gap: 8px;
}

.mobile-log-btn {
  width: 44px;
  height: 44px;
  background: linear-gradient(135deg, #1890ff, #096dd9);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 20px;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(24, 144, 255, 0.3);
}

.mobile-add-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: linear-gradient(135deg, #52c41a, #389e0d);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(82, 196, 26, 0.3);
}

.mobile-add-btn:active {
  transform: scale(0.98);
}

.plus-icon {
  font-size: 18px;
  font-weight: bold;
}

.mobile-task-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.mobile-task-card {
  background: white;
  border-radius: 12px;
  padding: 16px;
  border: 2px solid #f0f0f0;
  transition: all 0.3s;
}

.mobile-task-card.task-running {
  border-color: #52c41a;
  background: linear-gradient(135deg, #f6ffed, #f6ffed);
}

.task-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.task-name {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.task-status {
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
}

.task-info {
  margin-bottom: 16px;
}

.info-row {
  display: flex;
  align-items: flex-start;
  margin-bottom: 8px;
  font-size: 13px;
}

.info-label {
  color: #666;
  min-width: 70px;
  margin-right: 8px;
}

.info-value {
  color: #333;
  flex: 1;
  word-break: break-all;
}

.path-text {
  font-family: 'Menlo', 'Monaco', 'Courier New', monospace;
  font-size: 12px;
  color: #666;
}

.task-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.action-group {
  display: flex;
  gap: 8px;
}

.mobile-action-btn {
  flex: 1;
  padding: 10px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.start-btn {
  background: linear-gradient(135deg, #1890ff, #096dd9);
  color: white;
}

.stop-btn {
  background: linear-gradient(135deg, #ff4d4f, #cf1322);
  color: white;
}

.edit-btn {
  background: linear-gradient(135deg, #faad14, #d48806);
  color: white;
}

.delete-btn {
  background: linear-gradient(135deg, #ff4d4f, #cf1322);
  color: white;
}

.mobile-action-btn:active {
  transform: scale(0.95);
}

/* 移动端空状态样式 */
.mobile-empty-state {
  text-align: center;
  padding: 40px 20px;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.empty-text {
  color: #666;
  font-size: 16px;
  margin-bottom: 24px;
}

.empty-btn {
  margin: 0 auto;
}

/* 移动端对话框样式 */
.mobile-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: none;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 16px;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.mobile-task-dialog,
.mobile-directory-dialog,
.mobile-log-dialog {
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 500px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from { transform: translateY(50px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

.mobile-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  border-bottom: 1px solid #eee;
  position: sticky;
  top: 0;
  background: white;
  z-index: 1;
  border-radius: 16px 16px 0 0;
}

.mobile-dialog-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.mobile-close-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: #999;
  cursor: pointer;
  padding: 4px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.mobile-close-btn:active {
  background: #f5f5f5;
}

.mobile-dialog-content {
  padding: 20px;
  overflow-y: auto;
  flex-grow: 1;
}

.mobile-form-group {
  margin-bottom: 20px;
}

.mobile-form-group label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.mobile-form-input {
  width: 100%;
  padding: 14px;
  border: 2px solid #e8e8e8;
  border-radius: 10px;
  font-size: 15px;
  transition: all 0.3s;
  background: #fafafa;
}

.mobile-form-input:focus {
  border-color: #1890ff;
  background: white;
  outline: none;
  box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.1);
}

.mobile-path-input {
  display: flex;
  gap: 8px;
}

.mobile-select-btn {
  white-space: nowrap;
  padding: 14px 20px;
  background: linear-gradient(135deg, #faad14, #d48806);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}

.mobile-select-btn:active {
  transform: scale(0.98);
}

.mobile-password-input {
  display: flex;
  gap: 8px;
}

.mobile-toggle-password {
  white-space: nowrap;
  padding: 14px;
  background: #f0f0f0;
  border: 2px solid #e8e8e8;
  border-radius: 10px;
  font-size: 14px;
  cursor: pointer;
}

.mobile-password-hint {
  font-size: 12px;
  color: #999;
  margin-top: 6px;
}

.mobile-dialog-actions {
  display: flex;
  gap: 12px;
  padding: 20px;
  border-top: 1px solid #eee;
  position: sticky;
  bottom: 0;
  background: white;
  border-radius: 0 0 16px 16px;
}

.mobile-dialog-btn {
  flex: 1;
  padding: 16px;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
}

.mobile-dialog-btn.cancel {
  background: #f5f5f5;
  border: 2px solid #f0f0f0;
  color: #666;
}

.mobile-dialog-btn.confirm {
  background: linear-gradient(135deg, #1890ff, #096dd9);
  color: white;
  border: none;
}

.mobile-dialog-btn.full-width {
  width: 100%;
}

.mobile-dialog-btn.confirm:disabled {
  background: linear-gradient(135deg, #ccc, #bbb);
  cursor: not-allowed;
}

.mobile-dialog-btn:active:not(:disabled) {
  transform: scale(0.98);
}

/* 移动端文件夹选择对话框样式 */
.mobile-directory-content {
  padding: 20px;
  flex-grow: 1;
  overflow-y: auto;
}

.mobile-directory-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.mobile-directory-item {
  display: flex;
  align-items: center;
  padding: 14px;
  border: 2px solid #f0f0f0;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s;
}

.mobile-directory-item.selected {
  border-color: #1890ff;
  background: #e6f7ff;
}

.mobile-directory-item:active {
  transform: scale(0.98);
}

.mobile-folder-icon {
  font-size: 20px;
  margin-right: 12px;
  color: #faad14;
}

.mobile-directory-name {
  flex: 1;
  font-size: 15px;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mobile-enter-btn {
  background: none;
  border: none;
  font-size: 18px;
  color: #999;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
}

.mobile-enter-btn:active {
  background: #f5f5f5;
}

.mobile-empty-directory {
  text-align: center;
  color: #999;
  padding: 40px 20px;
  font-size: 15px;
}

.mobile-manual-input {
  padding: 20px 20px 0;
}

.mobile-input-controls {
  display: flex;
  gap: 8px;
}

.mobile-directory-input {
  flex: 1;
  padding: 14px;
  border: 2px solid #e8e8e8;
  border-radius: 10px;
  font-size: 15px;
}

.mobile-confirm-btn {
  white-space: nowrap;
  padding: 14px 20px;
  background: linear-gradient(135deg, #52c41a, #389e0d);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}

.mobile-directory-footer {
  padding: 20px;
  border-top: 1px solid #eee;
}

.mobile-selected-path {
  font-size: 13px;
  color: #666;
  margin-bottom: 16px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  word-break: break-all;
}

.mobile-action-buttons {
  display: flex;
  gap: 12px;
}

.mobile-nav-btn {
  background: linear-gradient(135deg, #f5f5f5, #e8e8e8);
  border: none;
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 14px;
  color: #666;
  cursor: pointer;
}

.mobile-nav-btn:active {
  transform: scale(0.98);
}

/* 移动端日志对话框样式 */
.mobile-log-toolbar {
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
  background: #fafafa;
}

.mobile-log-stats {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  font-size: 13px;
  color: #666;
}

.mobile-auto-refresh {
  display: flex;
  align-items: center;
  gap: 8px;
}

.mobile-switch {
  position: relative;
  display: inline-block;
  width: 36px;
  height: 18px;
}

.mobile-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.mobile-switch .slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: .4s;
  border-radius: 18px;
}

.mobile-switch .slider:before {
  position: absolute;
  content: "";
  height: 14px;
  width: 14px;
  left: 2px;
  bottom: 2px;
  background-color: white;
  transition: .4s;
  border-radius: 50%;
}

.mobile-switch input:checked + .slider {
  background-color: #1890ff;
}

.mobile-switch input:checked + .slider:before {
  transform: translateX(18px);
}

.mobile-log-actions {
  display: flex;
  gap: 8px;
}

.mobile-log-btn {
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 8px;
  font-size: 18px;
  cursor: pointer;
  transition: all 0.2s;
}

.mobile-log-btn.refresh {
  background: #e6f7ff;
  color: #1890ff;
}

.mobile-log-btn.download {
  background: #f6ffed;
  color: #52c41a;
}

.mobile-log-btn.clear {
  background: #fff2f0;
  color: #ff4d4f;
}

.mobile-log-btn:active {
  transform: scale(0.95);
}

.mobile-log-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.mobile-log-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
  background: #fafafa;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 12px;
  line-height: 1.5;
  min-height: 200px;
  max-height: 300px;
}

.mobile-empty-log {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #999;
  text-align: center;
}

.mobile-log-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.mobile-log-item {
  padding: 12px;
  background: white;
  border-radius: 8px;
  border-left: 4px solid transparent;
}

.mobile-log-item.log-level-error {
  background: #fff2f0;
  border-left-color: #ff4d4f;
}

.mobile-log-item.log-level-warn {
  background: #fffbe6;
  border-left-color: #faad14;
}

.mobile-log-item .log-header {
  display: flex;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 11px;
}

.mobile-log-item .log-time {
  color: #999;
}

.mobile-log-item .log-message {
  font-size: 12px;
  color: #333;
  word-break: break-all;
}

.mobile-log-pagination {
  padding: 16px 20px;
  border-top: 1px solid #eee;
  background: #fafafa;
}

.mobile-pagination-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.mobile-page-btn {
  width: 36px;
  height: 36px;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  background: white;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.2s;
}

.mobile-page-btn:hover:not(:disabled) {
  border-color: #1890ff;
  color: #1890ff;
}

.mobile-page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.mobile-page-info {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: #666;
}

.mobile-page-select {
  padding: 6px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  font-size: 13px;
  outline: none;
}

.mobile-page-select:focus {
  border-color: #1890ff;
}

/* 状态颜色类 */
.status-running {
  background: linear-gradient(135deg, #d4edda, #c3e6cb);
  color: #155724;
}

.status-stopped {
  background: linear-gradient(135deg, #f8d7da, #f5c6cb);
  color: #721c24;
}

.status-syncing {
  background: linear-gradient(135deg, #cce5ff, #b8daff);
  color: #004085;
}

/* ========== 响应式切换 ========== */
@media (max-width: 768px) {
  .desktop-view {
    display: none !important;
  }
  
  .mobile-view {
    display: block !important;
  }
  
  .dialog-overlay {
    display: none !important;
  }
  
  .mobile-dialog-overlay {
    display: flex !important;
  }
  
  .file-sync-container {
    padding: 16px;
    background: linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%);
  }
}

@media (min-width: 769px) {
  .desktop-view {
    display: block !important;
  }
  
  .mobile-view {
    display: none !important;
  }
  
  .dialog-overlay {
    display: flex !important;
  }
  
  .mobile-dialog-overlay {
    display: none !important;
  }
}
</style>